// Feature flags — single source of truth for flag names, defaults, labels, and merge logic.
//
// ## How a flag is resolved (three-tier cascade, later wins)
//
//   DEFAULT_FEATURES  →  VITE_FEATURE_* env overrides  →  tenant DB flags (tenants.feature_flags)
//
// The single sanctioned read path in app code is `useTenant().features`
// (context/TenantContext.tsx), which calls `getFeatures(tenant.featureFlags)`.
// Never read `tenant.featureFlags` directly in components — that skips the
// defaults and env tiers.
//
// ## There is NO "marketing site" flag
//
// The old marketing_site/publicSite flag was split in two and recurring
// confusion stems from conflating them:
//   - `tenants.is_published` — a COLUMN, the tenant-owned publish toggle for
//     their public landing page (migration 20260417000001).
//   - `sell_courses` — a feature FLAG, the operator entitlement for paid
//     checkout / pricing controls (renamed from `payments` in 20260423000001).
//
// ## fk_ prefix = framework kill-switch
//
// `fk_*` flags stage framework-level rollouts (e.g. learning_interactions
// dual-write). Their truth deliberately lives in the DB ONLY: they have no
// env override here, and services/learningInteractionService.ts reads them
// raw from tenants.feature_flags by design — so flipping the DB row is an
// instant, env-independent kill switch.

/** Canonical flag defaults. Every flag name must appear here. */
export const DEFAULT_FEATURES = {
  sell_courses: true,
  adminReporting: true,
  gamification: true,
  studentGradebook: true,
  skillsSystem: true,
  leadCapture: false,
  utilityPages: false,
  canvaSlide: false,
  forceLandscape: false,
  selfRegistration: true,
  sharedInstructorView: true,
  // Scenario builder family — opt-in operator flags. fk_ prefix denotes a
  // framework-level kill-switch (rollout staging); v1 suffix denotes a
  // user-visible feature in active beta.
  fk_learning_interactions: false,
  scenario_builder_v1: false,
  // Revision Builder — flag-gated, separate authoring surface for exam-style
  // source-examination revision modules. NOT part of the standard AI Create
  // interaction picker. Off by default; switched on per tenant in the hub.
  revision_builder: false,
  // Phase 9 — Experiences table rollout.
  // fk_experiences_table gates dual-write to the new table + viewer/stamp read flip.
  // fk_gradebook_via_experiences gates the separate gradebook reader migration (9g).
  fk_experiences_table: false,
  fk_gradebook_via_experiences: false,
  // Attune — personalised AI learning moment (Hook / Facilitator / Experience).
  // `attune` is the per-tenant entitlement; `fk_attune_capture` is the framework
  // kill-switch for persisting diagnostic/facilitation capture (DB-truth only).
  attune: false,
  fk_attune_capture: false,
  // Course bundles — sell/share a group of courses as one product. Off by
  // default; switched on per tenant in the hub. Gates the admin Bundles page,
  // the catalog Bundles section and the /bundle + bundle-checkout routes.
  bundles: false,
  // Hide a course's own catalogue card while it sits in a PUBLISHED bundle
  // (lib/catalogShelf.ts). Was unconditional; made opt-in and defaulted OFF in
  // Aug 2026 because a course silently vanishing from the shelf when someone
  // publishes a pack containing it reads as a bug from both sides — the author
  // can't find their own course, and a student who only wants that topic can't
  // reach it except through a pack. Turn it on per tenant if the duplication
  // genuinely bothers them.
  bundle_shelf_dedupe: false,
  // Kula Create — RETIRED (Aug 2026, superseded by Activity Doors below).
  // Whether this tenant can author Create experiences (the
  // `/instructor/create/…` walkthrough plus the "Experience" entry in the
  // builder's add menu). DEFAULT-OFF since retirement: doors are the standard
  // interactive route; Create stays intact behind this flag (switch it on per
  // tenant in the hub to revisit it). Turning it off hides the entry points AND
  // bounces the route; existing built experiences keep rendering, and leftover
  // interactive-slot slides degrade to inert stubs (CourseBuilder handles this).
  //
  // The two flags below assume you're already inside it: `create_library`
  // chooses WHICH Step 2 you get, `create_composition` adds the Compose fork.
  // Both are irrelevant while this is off.
  experience_builder: false,
  // Kula Create composition (Phase 1) — the Workshop surface: AI/designer
  // assembles bespoke experiences from capturing primitive blocks that render as
  // native Kula components. Off by default; partial work merges safely behind it.
  //
  // Scope is exactly the Compose fork — the "Compose a custom experience" card
  // in Step 2 and the compose pipeline behind it. It used to also gate the
  // lesson-source opt-in in Step 1, which made that checkbox appear on the
  // Library path (where Compose itself is suppressed) — an overlap with no
  // meaning. The opt-in is a Create feature, so it now stands on its own.
  create_composition: false,
  // The Library (KULA-CREATE-LIBRARY-PLAN.md) — Create's shelf model: named
  // world+mechanic experiences with Analyst matchmaker verdicts and an honest
  // no-fit exit. Also gates the Course Builder scout. When on, the Compose card
  // leaves the user path (composition becomes the internal Workshop).
  //
  // DEFAULT-ON since Aug 2026, which inverts what this flag means day to day: it
  // is no longer "opt into the Library", it is the standard Create path, and
  // turning it OFF drops that tenant back to the retired 18-mechanism chooser.
  // The catalogue was set aside because choosing from eighteen mechanisms and
  // then from three sketches asked authors to make design decisions they had no
  // basis for, and the ranking that fed it never produced picks worth the
  // complexity. Kept, not deleted — see "The catalogue is retired" in
  // KULA-CREATE-LIBRARY-PLAN.md for what's frozen and how to bring it back.
  create_library: true,
  // Single-call generation (Aug 2026) — the Create walkthrough's generation
  // runs as ONE model call (brief + small constitution + the type's data
  // contract) instead of the design-concepts → design-spec → build → qa relay.
  // Same UI, same importers/validators/renderers, roughly a fifth of the
  // per-build API spend. DEFAULT-ON since the same-brief head-to-head showed
  // the relay was flattening the writing; OFF is the escape hatch back to the
  // staged relay while it still exists.
  create_direct_build: true,
  // Per-slide theme-world skins for system slides (true/false, quiz, scale) —
  // Tier B atmosphere wrap with a swatch picker in the slide inspector.
  slide_skins: false,
  // Activity Doors (KULA-ACTIVITY-DOORS-TOP10.md) — the STANDARD interactive
  // route (Aug 2026): five named activity slide types (Scenario, Case Study,
  // Mark the Work, Debate, Card Sort), each a structured template that
  // generates a sealed self-contained interactive from a brief. DEFAULT-ON —
  // this and the Lesson Designer's doors mode go together: with it on, the
  // Designer recommends named door slides; off drops that tenant back to the
  // retired typeless Interactive-slot prompt branch (kept for flag-off tenants,
  // do not extend it).
  activity_doors: true,
  // Learner Model (LEARNER_MODEL_PLAN.md) — memory as the product spine.
  // `memory` is the per-tenant entitlement (the two delightful moments:
  // growth reflection at completion, cohort read in Learning Insights);
  // `fk_memory` is the framework kill-switch for profile synthesis writes
  // (DB-truth only, like fk_attune_capture).
  memory: false,
  fk_memory: false,
  // Essay Marking — the CAPS History marking engine as a slide type. Gates the
  // "Essay Marking" tile in the builder's Interactive row.
  //
  // Off by default and deliberately narrow: the engine marks South African NSC
  // Grade 12 History essays against specific past papers with their own memos
  // and misconceptions brains, so the tile is noise in any tenant not teaching
  // that syllabus.
  //
  // AUTHORING GATE, plus one learner-facing READ. What bounds a learner is the
  // marking allowance (lib/marking/allowance.ts): /api/ai/mark-essay holds one
  // before it spends anything, so a tenant with no grants gets a 402 whatever
  // this flag says. Turning it on does not hand anyone free markings.
  //
  // The read that is not authoring: StudentGradebook shows its Essay Reports tab
  // when this is on (or when the learner already has a report). That is asking
  // "does this tenant mark essays?" rather than "may this person author one",
  // and it is deliberate — without it the tab only appeared once a report
  // existed, so a learner before their first marking saw no tab at all and the
  // panel's own empty state could never render. AdminStudentDetail reads it the
  // same way for its markings tab. Anything gating a learner's ENTITLEMENT still
  // belongs in the allowance, not here.
  essay_marking: false,
  // School passes — sell all-access to an entire school with one redeem code
  // (read-time entitlement, 20260901000004). Gates /admin/schools, /redeem
  // and the pass-coverage ownership treatment. Off by default; switched on
  // per tenant in the hub (built for Revise).
  school_passes: false,
} satisfies Record<string, boolean>;

/** Canonical flag names — a typo'd key is a compile error at typed call sites. */
export type FeatureKey = keyof typeof DEFAULT_FEATURES;

/** Human-readable labels for admin UIs. */
export const FEATURE_LABELS: Record<FeatureKey, string> = {
  sell_courses: 'Sell courses',
  adminReporting: 'Admin Reporting',
  gamification: 'Gamification',
  studentGradebook: 'Student Gradebook',
  skillsSystem: 'Skills & Mastery',
  leadCapture: 'Lead Capture',
  utilityPages: 'Utility Pages',
  canvaSlide: 'Canva Slide Integration',
  forceLandscape: 'Force Landscape on Mobile',
  selfRegistration: 'Self Registration',
  sharedInstructorView: 'Shared Instructor View',
  fk_learning_interactions: 'Learning Interactions dual-write',
  scenario_builder_v1: 'AI Scenario Builder (beta)',
  revision_builder: 'Revision Builder (exam source modules)',
  fk_experiences_table: 'Experiences table dual-write',
  fk_gradebook_via_experiences: 'Gradebook reads via experiences',
  attune: 'Attune (personalised AI moment)',
  fk_attune_capture: 'Attune capture dual-write',
  bundles: 'Course bundles',
  bundle_shelf_dedupe: 'Hide bundled courses from the catalogue',
  experience_builder: 'Experience Builder (Kula Create — retired, opt-in)',
  create_composition: 'Kula Create composition (Workshop)',
  create_library: 'Kula Create Library (off = retired chooser)',
  create_direct_build: 'Kula Create direct build (single-call generation)',
  slide_skins: 'Per-slide theme-world skins',
  activity_doors: 'Activity Doors (standard interactive slides)',
  memory: 'Learner Model (memory moments)',
  fk_memory: 'Learner profile synthesis writes',
  essay_marking: 'Essay Marking (CAPS History)',
  school_passes: 'School passes (site licences)',
};

/**
 * Flags whose surfaces have been superseded but are kept alive for tenants
 * that still use them. The Hub folds these into a collapsed "Retired" shelf at
 * the foot of their group, so the day-to-day list only shows the flags an
 * operator should actually be deciding — a retired flag is still fully
 * functional, just filed away. Value = the one line shown in the shelf: what
 * the flag was and what superseded it.
 */
export const RETIRED_FEATURES: Partial<Record<FeatureKey, string>> = {
  scenario_builder_v1:
    'Slide-level AI build action from the staged Create pipeline. Superseded by Activity Doors.',
  experience_builder:
    'Kula Create (retired Aug 2026, superseded by Activity Doors). On restores the Create walkthrough and its entry points for this tenant.',
  create_library:
    'Only matters inside Kula Create: which Step 2 an opted-in tenant gets. Leave on — off is the even older 18-mechanism chooser.',
  create_direct_build:
    'Only matters inside Kula Create: single-call generation vs the staged relay. Leave on.',
  create_composition:
    'Only matters inside Kula Create: the Compose fork (Workshop surface).',
};

/** Short descriptions shown alongside toggles in admin UIs. */
export const FEATURE_DESCRIPTIONS: Record<FeatureKey, string> = {
  sell_courses:
    'Charge for courses via Paystack checkout. Off: catalogue is free-enrol only and pricing controls are hidden.',
  adminReporting:
    'Show the Admin → Reports area (engagement, completion, revenue dashboards).',
  gamification:
    'Points, badges, streaks, levels and leaderboards across the student experience.',
  studentGradebook:
    'Per-student gradebook view of quiz scores and activity submissions for admins and instructors.',
  skillsSystem:
    'Tag slides with skills and surface skill mastery across courses on the student dashboard.',
  leadCapture:
    'Collect prospect details from the public catalogue and store them in the Leads table.',
  utilityPages:
    'Expose optional utility pages (legal, help, contact templates) in the student footer.',
  canvaSlide:
    'Allow instructors to add Canva embed slides in the course builder.',
  forceLandscape:
    'Require landscape orientation on mobile devices when viewing lessons.',
  selfRegistration:
    'Let visitors create their own student accounts on the login page. Off: admins must invite users.',
  sharedInstructorView:
    'Let admins view and edit any instructor\u2019s courses, not just their own.',
  fk_learning_interactions:
    'Dual-write every quiz/activity/reflection submission into the new learning_interactions table alongside the existing per-template tables. Required for the AI Scenario Builder to capture learner data. Phase 0 of the Learning Interaction Framework.',
  scenario_builder_v1:
    'Surface the \u201cAI build\u201d action on supported scenario-template slides. Walks the author through Analyst \u2192 3 concepts \u2192 Designer Spec \u2192 Builder \u2192 QA. Requires AI credits on the tenant.',
  revision_builder:
    'Enable the Revision Builder \u2014 a separate authoring surface for exam-style revision modules (a source document plus scaffolded L1/L2/L3 questions with built-in skills coaching). Built for source-based subjects (History, English comprehension, RS). Not part of the standard AI interaction picker.',
  fk_experiences_table:
    'Phase 9 framework gate. When on, applying a scenario group dual-writes to the new experiences table alongside the legacy content.scenarioGroups[] JSONB, and the lesson viewer resolves group state from the table first. learning_interactions rows from scenario-group slides also get experience_id stamped. Off = legacy JSONB path only.',
  fk_gradebook_via_experiences:
    'Phase 9 \u00a7g. When on, the gradebook (student + admin views) reads from learning_interactions rolled up by experience_id instead of the legacy quiz_attempts / activity_responses RPCs. Requires fk_experiences_table to also be on for the same tenant.',
  attune:
    'Enable Attune \u2014 a personalised AI learning moment a designer drops into a lesson. Behaves as Hook (start), Facilitator (middle) or Experience (end). Requires AI credits on the tenant.',
  fk_attune_capture:
    'Framework kill-switch for persisting Attune capture (diagnostic transcripts, chosen modality, reflections, facilitation transcripts) to the tenant-scoped Attune tables. DB-truth only.',
  bundles:
    'Sell or share groups of courses as one product. Adds a Bundles page to the tenant admin, a Bundles section to the student catalogue, and bundle checkout. Enrolling in a bundle grants every course in it.',
  bundle_shelf_dedupe:
    "Stop a course showing its own catalogue card while it sits in a published bundle, so each product appears once. OFF by default: most tenants would rather a course stayed findable on its own than disappear the moment it joins a pack. Suspended under search and tag filters either way, since the bundle isn't on screen then.",
  experience_builder:
    'Kula Create, retired Aug 2026 (Activity Doors are the standard now). ON: the "Experience" option returns to the course builder\'s add menu and the Create walkthrough opens. OFF (default): those entry points are hidden and the /instructor/create route turns visitors away — existing experiences keep rendering and playing for learners, they just can\'t be built or re-opened for editing. The Library and Workshop flags below only matter when this is on. Requires AI credits on the tenant.',
  create_composition:
    'Kula Create Phase 1 — the Workshop. Lets the AI (and designer) assemble bespoke experiences from capturing primitive blocks (stimulus, choice, rate-slider, sort-zones, select-evidence, reveal, commit-box) that render as native Kula components and report readable learner data. Additive: the existing interaction Library is untouched. Off by default.',
  create_library:
    'ON (standard): Create offers the Library — named, audited experiences with per-entry fit verdicts from the Analyst matchmaker and an honest no-fit exit to standard slides. One screen, one pick. OFF: this tenant falls back to the retired 18-mechanism chooser (pick a mechanism, then pick one of three sketches), kept only as an escape hatch for an author who needs a mechanism the shelf does not carry. Either way, requires AI credits on the tenant.',
  create_direct_build:
    'Generation engine swap inside the Create walkthrough: the build runs as a single model call (the author\'s brief plus a short constitution and the experience type\'s data contract) instead of the staged concepts → spec → build → QA relay. Same steps on screen, same playtest and refine, same structural validation; noticeably cheaper and, on the Aug 2026 head-to-head, better writing. Off = the staged relay (legacy fallback).',
  slide_skins:
    'Let a single system slide (true/false, quiz, scale) wear a theme world via a swatch picker in the slide inspector. Look, sound and feedback dress only — the mechanic, wording, scoring and capture never change. Worlds that cannot honestly dress a slide type (e.g. arcade win/lose on a no-right-answer scale) are refused in the picker.',
  activity_doors:
    'The standard interactive slides — five named activity doors in the slide picker (Scenario, Case Study, Mark the Work, Debate, Card Sort). Each opens a short intake (context, goal, length, theme world) and generates a self-contained interactive; the Lesson Designer recommends them as named door slides in its outlines. Requires AI credits on the tenant. Off: falls back to the retired Interactive-slot flow.',
  memory:
    'Enable the Learner Model moments — a synthesised growth reflection at course completion (learner-facing, correctable) and the “Your cohort, in a paragraph” read in Learning Insights. Requires AI credits on the tenant; synthesis also needs fk_memory on.',
  fk_memory:
    'Framework kill-switch for learner-profile synthesis — the server-side LLM calls that create and update learner_profiles rows. DB-truth only. Off = no profile is written or updated, even where the memory entitlement is on.',
  essay_marking:
    'Show the Essay Marking slide type in the builder. The engine marks South African NSC Grade 12 History essays against specific past papers, so it is noise for any tenant not teaching that syllabus. Authoring gate only — a learner is bounded by their marking allowance, and a tenant with no grants gets a 402 regardless of this flag.',
  school_passes:
    'Sell all-access to an entire school: admins create a school and issue a redeem code with a seat cap and end date (/admin/schools); learners redeem it once (/redeem) and every covered course opens without payment until the pass ends. Access is checked at read time, so newly published courses are automatically included.',
};

/**
 * Grouping for admin UIs (the Hub tenant editor renders one section per group).
 * Order within `keys` is display order. Every FeatureKey must appear in exactly
 * one group — the `_assertAllFlagsGrouped` check below makes a missing key a
 * compile error, so adding a flag forces you to choose its group.
 */
export const FEATURE_GROUPS = [
  {
    id: 'commerce',
    title: 'Commerce',
    description: 'Selling and prospect capture.',
    keys: ['sell_courses', 'bundles', 'bundle_shelf_dedupe', 'school_passes', 'leadCapture'],
  },
  {
    id: 'student',
    title: 'Student experience',
    description: 'What learners see and can do.',
    keys: [
      'gamification',
      'skillsSystem',
      'studentGradebook',
      'memory',
      'selfRegistration',
      'forceLandscape',
      'utilityPages',
    ],
  },
  {
    id: 'authoring',
    title: 'Authoring & AI tools',
    description: 'Instructor-facing builders. The AI tools spend tenant AI credits.',
    keys: [
      'sharedInstructorView',
      'canvaSlide',
      // The standard interactive route leads the AI tools.
      'activity_doors',
      'slide_skins',
      'revision_builder',
      'essay_marking',
      'attune',
      // Retired Create-era flags (RETIRED_FEATURES) — the Hub files these into
      // a collapsed shelf at the foot of the group. Gate first, then the flags
      // that only mean something once you're through it.
      'scenario_builder_v1',
      'experience_builder',
      'create_library',
      'create_direct_build',
      'create_composition',
    ],
  },
  {
    id: 'admin',
    title: 'Admin',
    description: 'Tenant-admin dashboards.',
    keys: ['adminReporting'],
  },
  {
    id: 'framework',
    title: 'Framework kill-switches',
    description:
      'Internal rollout staging (fk_*) — not tenant features. Leave alone unless you are executing a rollout plan; flipping these changes how learner data is written and read.',
    keys: [
      'fk_learning_interactions',
      'fk_experiences_table',
      'fk_gradebook_via_experiences',
      'fk_attune_capture',
      'fk_memory',
    ],
  },
] as const satisfies ReadonlyArray<{
  id: string;
  title: string;
  description: string;
  keys: readonly FeatureKey[];
}>;

// Compile-time exhaustiveness: if a FeatureKey is missing from every group,
// the Record below has a required key with type `never` that `{}` can't satisfy.
type GroupedKey = (typeof FEATURE_GROUPS)[number]['keys'][number];
const _assertAllFlagsGrouped: Record<Exclude<FeatureKey, GroupedKey>, never> = {};
void _assertAllFlagsGrouped;

// Env-var overrides (compile-time). Default-ON flags use `!== 'false'` (opt-out),
// default-OFF flags use `=== 'true'` (opt-in). fk_* flags are deliberately
// absent — they are DB-truth kill-switches (see doc block above).
const ENV_OVERRIDES: Partial<Record<FeatureKey, boolean>> = {
  sell_courses: import.meta.env.VITE_FEATURE_SELL_COURSES !== 'false',
  adminReporting: import.meta.env.VITE_FEATURE_ADMIN_REPORTING !== 'false',
  gamification: import.meta.env.VITE_FEATURE_GAMIFICATION !== 'false',
  studentGradebook: import.meta.env.VITE_FEATURE_GRADEBOOK !== 'false',
  skillsSystem: import.meta.env.VITE_FEATURE_SKILLS !== 'false',
  leadCapture: import.meta.env.VITE_FEATURE_LEAD_CAPTURE === 'true',
  utilityPages: import.meta.env.VITE_FEATURE_UTILITY_PAGES === 'true',
  canvaSlide: import.meta.env.VITE_FEATURE_CANVA_SLIDE === 'true',
  forceLandscape: import.meta.env.VITE_FEATURE_FORCE_LANDSCAPE === 'true',
  selfRegistration: import.meta.env.VITE_FEATURE_SELF_REGISTRATION !== 'false',
  sharedInstructorView: import.meta.env.VITE_FEATURE_SHARED_INSTRUCTOR_VIEW !== 'false',
  scenario_builder_v1: import.meta.env.VITE_FEATURE_SCENARIO_BUILDER === 'true',
  revision_builder: import.meta.env.VITE_FEATURE_REVISION_BUILDER === 'true',
  attune: import.meta.env.VITE_FEATURE_ATTUNE === 'true',
  bundles: import.meta.env.VITE_FEATURE_BUNDLES === 'true',
  bundle_shelf_dedupe: import.meta.env.VITE_FEATURE_BUNDLE_SHELF_DEDUPE === 'true',
  // Opt-in (retired, default-OFF): set VITE_FEATURE_EXPERIENCE_BUILDER=true in
  // .env.local to revisit Kula Create locally without a DB change.
  experience_builder: import.meta.env.VITE_FEATURE_EXPERIENCE_BUILDER === 'true',
  // Opt-in: set VITE_FEATURE_CREATE_COMPOSITION=true in .env.local to switch the
  // Studio (composition) on for local dev without a DB change. Off in prod.
  create_composition: import.meta.env.VITE_FEATURE_CREATE_COMPOSITION === 'true',
  // Opt-OUT now that the Library is the standard path: set
  // VITE_FEATURE_CREATE_LIBRARY=false to get the retired chooser back locally.
  create_library: import.meta.env.VITE_FEATURE_CREATE_LIBRARY !== 'false',
  // Opt-OUT (default-ON): set VITE_FEATURE_CREATE_DIRECT_BUILD=false to test
  // the staged relay locally.
  create_direct_build: import.meta.env.VITE_FEATURE_CREATE_DIRECT_BUILD !== 'false',
  slide_skins: import.meta.env.VITE_FEATURE_SLIDE_SKINS === 'true',
  // Opt-OUT now that doors are the standard: set VITE_FEATURE_ACTIVITY_DOORS=false
  // to test the retired Interactive-slot path locally.
  activity_doors: import.meta.env.VITE_FEATURE_ACTIVITY_DOORS !== 'false',
  memory: import.meta.env.VITE_FEATURE_MEMORY === 'true',
  school_passes: import.meta.env.VITE_FEATURE_SCHOOL_PASSES === 'true',
};

/**
 * Merge defaults → env vars → tenant DB flags.
 * Tenant flags take highest precedence.
 */
export function getFeatures(tenantFlags?: Record<string, boolean>): Record<FeatureKey, boolean> {
  return {
    ...DEFAULT_FEATURES,
    ...ENV_OVERRIDES,
    ...(tenantFlags || {}),
  };
}
